import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ShieldCheck, Eye, EyeOff, CheckCircle2, XCircle } from 'lucide-react';

const FOREST = '#2C4A3B';
const FOREST_DARK = '#1E3329';
const TERRA = '#C47A5E';
const CREAM = '#F9F6F0';
const CHARCOAL = '#1C1917';

// Backend policy (server.py _password_strength_error):
//   • ≥12 characters  • 3 of 4 classes (lower/upper/digit/symbol)
//   • No common breached stems (password, qwerty, plexura, …)
//   • No 4-char repeats or sequences
const BLOCKED_STEMS = [
  'password', 'passw0rd', 'qwerty', 'asdfgh', 'abcdef',
  'letmein', 'welcome', 'iloveyou', 'admin', 'monkey',
  'dragon', 'master', 'trustno', 'starwars', 'plexura',
];

function checkStrength(pwd) {
  const lower = /[a-z]/.test(pwd);
  const upper = /[A-Z]/.test(pwd);
  const digit = /\d/.test(pwd);
  const symbol = /[^A-Za-z0-9]/.test(pwd);
  const classes = [lower, upper, digit, symbol].filter(Boolean).length;

  const normalized = pwd.toLowerCase().replace(/[^a-z]/g, '');
  const blocked = BLOCKED_STEMS.find((s) => normalized.includes(s));

  let sequence = false;
  for (let i = 0; i + 3 < pwd.length; i += 1) {
    const a = pwd.charCodeAt(i);
    if (pwd.charCodeAt(i + 1) === a && pwd.charCodeAt(i + 2) === a && pwd.charCodeAt(i + 3) === a) {
      sequence = true;
      break;
    }
    const diffs = [0, 1, 2, 3].map((j) => pwd.charCodeAt(i + j) - a);
    if (diffs.join(',') === '0,1,2,3' || diffs.join(',') === '0,-1,-2,-3') {
      sequence = true;
      break;
    }
  }

  return {
    length: pwd.length >= 12,
    classes: classes >= 3,
    notCommon: !blocked,
    notSequence: !sequence,
    blockedStem: blocked,
  };
}

export default function ResetPasswordPage({ onLogin }) {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const strength = checkStrength(pwd);
  const allMet = strength.length && strength.classes && strength.notCommon && strength.notSequence;
  const match = pwd.length > 0 && pwd === confirm;

  // Pydantic 422 errors arrive as { detail: [{ msg, loc, ... }, ...] }.
  // FastAPI HTTPException returns { detail: "string" }. Normalize both.
  const extractError = (err) => {
    const detail = err?.response?.data?.detail;
    if (!detail) return 'Reset failed. Please request a new link.';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      const first = detail[0];
      if (typeof first === 'string') return first;
      // Pydantic v2 wraps the policy error in "Value error, <msg>"
      const msg = first?.msg || '';
      return msg.replace(/^Value error,\s*/i, '') || 'Reset failed. Please request a new link.';
    }
    return 'Reset failed. Please request a new link.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allMet) {
      if (!strength.length) {
        toast.error('Password must be at least 12 characters long.');
      } else if (!strength.classes) {
        toast.error('Use 3 of: lowercase, uppercase, number, symbol.');
      } else if (!strength.notCommon) {
        toast.error(`Avoid common patterns like "${strength.blockedStem}".`);
      } else if (!strength.notSequence) {
        toast.error('Avoid simple sequences (e.g. "1234" or "aaaa").');
      }
      return;
    }
    if (!match) {
      toast.error("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/password-reset/confirm`, {
        token,
        new_password: pwd,
      });
      toast.success('Password updated — welcome back!');
      if (res.data.token && res.data.user) {
        onLogin?.(res.data.token, res.data.user);
        navigate('/');
      } else {
        navigate('/login');
      }
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  };

  const Criterion = ({ ok, label }) => (
    <div className="flex items-center gap-1.5 text-xs" style={{ color: ok ? FOREST : '#9A9A90' }}>
      {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
      <span>{label}</span>
    </div>
  );

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ fontFamily: "'Manrope', sans-serif", background: CREAM }}
    >
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-[0.06]" style={{ background: FOREST }} aria-hidden="true" />
      <div className="absolute -bottom-32 -left-20 w-80 h-80 rounded-full opacity-[0.08]" style={{ background: TERRA }} aria-hidden="true" />

      <div className="w-full max-w-md relative">
        <div className="rounded-3xl shadow-xl p-8 sm:p-10 border" style={{ background: '#fff', borderColor: '#E3E8E4' }}>
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-md" style={{ background: FOREST }}>
              <ShieldCheck className="w-8 h-8" style={{ color: CREAM }} />
            </div>
          </div>

          <h1 className="text-2xl sm:text-3xl font-bold text-center mb-2 tracking-tight" style={{ color: CHARCOAL }}>
            Set a new password
          </h1>
          <p className="text-center text-sm mb-8" style={{ color: '#6B8E7B' }}>
            Pick something strong — you'll be signed in right after.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
            <div>
              <Label htmlFor="new-password" className="text-sm font-medium" style={{ color: CHARCOAL }}>
                New Password
              </Label>
              <div className="relative mt-1.5">
                <Input
                  id="new-password"
                  type={showPwd ? 'text' : 'password'}
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  required
                  autoFocus
                  minLength={12}
                  data-testid="reset-new-password-input"
                  className="h-11 pr-11 rounded-xl focus:outline-none focus:ring-2"
                  style={{ borderColor: '#E3E8E4' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                  aria-label={showPwd ? 'Hide password' : 'Show password'}
                  data-testid="reset-toggle-pwd-visibility"
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1" data-testid="reset-strength-meter">
                <Criterion ok={strength.length} label="At least 12 characters" />
                <Criterion ok={strength.classes} label="3 of: a-z, A-Z, 0-9, symbol" />
                <Criterion ok={strength.notCommon} label="No common patterns" />
                <Criterion ok={strength.notSequence} label="No '1234' / 'aaaa'" />
              </div>
            </div>

            <div>
              <Label htmlFor="confirm-password" className="text-sm font-medium" style={{ color: CHARCOAL }}>
                Confirm Password
              </Label>
              <Input
                id="confirm-password"
                type={showPwd ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                data-testid="reset-confirm-password-input"
                className="mt-1.5 h-11 rounded-xl focus:outline-none focus:ring-2"
                style={{ borderColor: confirm && !match ? '#DC2626' : '#E3E8E4' }}
              />
              {confirm && !match && (
                <p className="mt-1 text-xs" style={{ color: '#DC2626' }}>Passwords don't match.</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading || !allMet || !match}
              data-testid="reset-submit"
              className="w-full h-11 rounded-xl text-white font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ background: loading ? FOREST_DARK : FOREST }}
            >
              {loading ? 'Updating…' : 'Update Password & Sign In'}
            </Button>
          </form>

          <p className="text-center text-sm mt-6" style={{ color: '#6B8E7B' }}>
            Link not working?{' '}
            <Link to="/forgot-password" className="font-semibold hover:underline" style={{ color: TERRA }} data-testid="request-new-link">
              Request a new one
            </Link>
          </p>
        </div>
        <p className="text-center text-xs mt-6" style={{ color: '#6B8E7B' }}>
          © Plexura AI Scribe · HIPAA-compliant mental health scribing
        </p>
      </div>
    </div>
  );
}
